import React, { useState, useEffect } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { DialogFooter } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const InstallationForm = ({ propertyId, installation, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(installation || {
    property_id: propertyId,
    installation_type: 'solar_panels',
    installation_date: '',
    panel_brand: '',
    panel_wattage: '',
    number_of_panels: '',
    inverter_brand: '',
    inverter_model: '',
    battery_brand: '',
    battery_capacity: '',
    charger_brand: '',
    charger_power: '',
    notes: ''
  });

  useEffect(() => {
    if (installation) {
      setFormData(installation);
    } else {
      setFormData({
        property_id: propertyId,
        installation_type: 'solar_panels',
        installation_date: '',
        panel_brand: '',
        panel_wattage: '',
        number_of_panels: '',
        inverter_brand: '',
        inverter_model: '',
        battery_brand: '',
        battery_capacity: '',
        charger_brand: '',
        charger_power: '',
        notes: ''
      });
    }
  }, [installation, propertyId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanedData = { property_id: propertyId };
    for (const [key, value] of Object.entries(formData)) {
      if (value !== '' && value !== null && value !== undefined) {
        cleanedData[key] = value;
      }
    }
    onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Type Installatie *</Label>
        <Select value={formData.installation_type} onValueChange={(v) => handleChange('installation_type', v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="solar_panels">Zonnepanelen</SelectItem>
            <SelectItem value="battery">Batterij</SelectItem>
            <SelectItem value="ev_charger">Laadpaal</SelectItem>
            <SelectItem value="inverter">Omvormer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Datum Plaatsing</Label>
        <Input
          type="date"
          value={formData.installation_date || ''}
          onChange={(e) => handleChange('installation_date', e.target.value)}
        />
      </div>

      {formData.installation_type === 'solar_panels' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merk Panelen</Label>
              <Input
                value={formData.panel_brand || ''}
                onChange={(e) => handleChange('panel_brand', e.target.value)}
                placeholder="bv. DMEGC, SunPower"
              />
            </div>
            <div className="space-y-2">
              <Label>Aantal Panelen</Label>
              <Input
                type="number"
                value={formData.number_of_panels || ''}
                onChange={(e) => handleChange('number_of_panels', parseInt(e.target.value) || '')}
                placeholder="bv. 10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Vermogen per Paneel (Wp)</Label>
            <Input
              type="number"
              value={formData.panel_wattage || ''}
              onChange={(e) => handleChange('panel_wattage', parseInt(e.target.value) || '')}
              placeholder="bv. 400"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merk Omvormer</Label>
              <Input
                value={formData.inverter_brand || ''}
                onChange={(e) => handleChange('inverter_brand', e.target.value)}
                placeholder="bv. GoodWe, SMA"
              />
            </div>
            <div className="space-y-2">
              <Label>Model Omvormer</Label>
              <Input
                value={formData.inverter_model || ''}
                onChange={(e) => handleChange('inverter_model', e.target.value)}
                placeholder="bv. 3.6 kW"
              />
            </div>
          </div>
        </>
      )}

      {formData.installation_type === 'battery' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Batterij</Label>
            <Input
              value={formData.battery_brand || ''}
              onChange={(e) => handleChange('battery_brand', e.target.value)}
              placeholder="bv. Tesla, LG"
            />
          </div>
          <div className="space-y-2">
            <Label>Capaciteit (kWh)</Label>
            <Input
              value={formData.battery_capacity || ''}
              onChange={(e) => handleChange('battery_capacity', e.target.value)}
              placeholder="bv. 13.5"
            />
          </div>
        </div>
      )}

      {formData.installation_type === 'ev_charger' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Laadpaal</Label>
            <Input
              value={formData.charger_brand || ''}
              onChange={(e) => handleChange('charger_brand', e.target.value)}
              placeholder="bv. Wallbox, Alfen"
            />
          </div>
          <div className="space-y-2">
            <Label>Vermogen (kW)</Label>
            <Input
              value={formData.charger_power || ''}
              onChange={(e) => handleChange('charger_power', e.target.value)}
              placeholder="bv. 11 kW"
            />
          </div>
        </div>
      )}

      {formData.installation_type === 'inverter' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Omvormer</Label>
            <Input
              value={formData.inverter_brand || ''}
              onChange={(e) => handleChange('inverter_brand', e.target.value)}
              placeholder="bv. GoodWe, SMA"
            />
          </div>
          <div className="space-y-2">
            <Label>Model Omvormer</Label>
            <Input
              value={formData.inverter_model || ''}
              onChange={(e) => handleChange('inverter_model', e.target.value)}
              placeholder="bv. 3.6 kW"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Notities</Label>
        <Textarea
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          placeholder="Extra opmerkingen..."
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Opslaan...' : (installation?.id ? 'Bijwerken' : 'Toevoegen')}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default InstallationForm;
